import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Numeric, Separator } from '@blade/ui-kit'
import { DATE_FORMAT } from '@root/shared'
import { AmountFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import { ExploreDevHoldingsInfo } from '../explore-dev-tooltip/explore-dev-holdings-info'

import type { PairModel } from '@root/entities/pair'
import type { TransKey } from '@root/shared/localization'

interface Props {
  pair: PairModel
}

const TitleMap: Record<'buy' | 'sell', TransKey> = {
  buy: 'explore.developer-swap-tooltip.buy',
  sell: 'explore.developer-swap-tooltip.sell',
}

export const ExploreDevSwapTooltip = () => {}

const ClickArea = (props: Props) => (
  <ExploreDevHoldingsInfo.ClickArea pair={props.pair} />
)

const CloseArea = (props: Props) => {
  const { t } = useTranslation()
  const [txCreator] = useAtom(props.pair.initTxCreator)
  const [audit] = useAtom(props.pair.audit)
  const [devSoldAll] = useAtom(props.pair.devSoldAllAtom)

  const title = txCreator?.lastTx?.operation
    ? t(TitleMap[txCreator.lastTx.operation])
    : undefined

  const lastTxAtFormatted = useMemo(() => {
    if (!txCreator?.lastTx?.at) {
      return undefined
    }
    return DateF.format(txCreator.lastTx.at, DATE_FORMAT.dd_MM_yy__HH_mm)
  }, [txCreator?.lastTx?.at])

  const renderAmount = () => {
    if (devSoldAll) {
      return <SoldOut>{t('explore.developer-swap-tooltip.sold-all')}</SoldOut>
    }

    const auditPercent = audit.checks.ownerTokenBalance.percent
    return (
      <>
        <Numeric value={auditPercent} preset="percents" />%
      </>
    )
  }

  return (
    <Root>
      <TitleGroup>
        <Title>{title}</Title>
        <Percent data-intent={audit.checks.ownerTokenBalance.status}>
          {renderAmount()}
        </Percent>
      </TitleGroup>
      <BodySeparator variant="solid" color="var(--backgroundA-3)" />
      <ExploreDevHoldingsInfo.CloseArea pair={props.pair} />
      {lastTxAtFormatted ? (
        <Item>
          <Key>{t('common.date')}</Key>
          <Value>{lastTxAtFormatted}</Value>
        </Item>
      ) : undefined}
      {txCreator?.lastTx?.amountInUsd ? (
        <Item>
          <Key>{t('common.amount')}</Key>
          <Value>
            <AmountFormatted
              amount={txCreator.lastTx.amountInUsd}
              currency="$"
            />
          </Value>
        </Item>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
`

const TitleGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
`

ExploreDevSwapTooltip.ClickArea = ClickArea
ExploreDevSwapTooltip.CloseArea = CloseArea

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--text-12);
`
const Percent = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  &[data-intent='Safe'] {
    color: var(--up-9);
  }
  &[data-intent='Danger'] {
    color: var(--down-9);
  }
  &[data-intent='Warning'] {
    color: var(--warning-9);
  }
`
const SoldOut = styled.span`
  color: var(--down-9);
`

const Item = styled.li`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
`
const Value = styled.div`
  color: var(--text-11);
`
const Key = styled.div`
  color: var(--text-10);
`

const BodySeparator = styled(Separator)`
  margin: 8px 0;
`
